import { useState, type FormEvent } from 'react';
import { Alert, Box, Button, Card, CardContent, Link, TextField, Typography } from '@mui/material';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { register } from '../api/auth';
import { useAuth } from '../store/AuthContext';

export function Register() {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const { roleHint: roleHintParam } = useParams<{ roleHint?: string }>();
  const roleHint: 'dev' | 'manager' = roleHintParam === 'manager' ? 'manager' : 'dev';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setSubmitting(true);
    try {
      const r = await register({ email, password, confirmPassword, inviteCode, roleHint });
      setSession(r.token, r.user);
      navigate('/dashboard', { replace: true });
    } catch (err: unknown) {
      const resp = (err as { response?: { status?: number; data?: { error?: string } } })?.response;
      const message =
        resp?.status === 409
          ? 'An account with this email already exists. Please sign in instead.'
          : resp?.data?.error ?? 'Registration failed';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
      <Card elevation={3} sx={{ width: 480 }}>
        <CardContent>
          <Typography variant="h4" component="h1" gutterBottom>
            Register
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Registering as a <strong>{roleHint === 'manager' ? 'manager' : 'developer'}</strong>.{' '}
            <Link
              component={RouterLink}
              to={`/register/${roleHint === 'manager' ? 'dev' : 'manager'}`}
              underline="always"
            >
              Switch to {roleHint === 'manager' ? 'developer' : 'manager'}
            </Link>
          </Typography>
          {error && (
            <Alert severity="error" role="alert" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Email *"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              variant="standard"
              fullWidth
            />
            <TextField
              label="Invite Code *"
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
              required
              variant="standard"
              fullWidth
              helperText="Provided by your team lead"
            />
            <TextField
              label="Password *"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              variant="standard"
              fullWidth
              helperText="At least 8 characters"
            />
            <TextField
              label="Confirm Password *"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              variant="standard"
              fullWidth
            />
            <Button type="submit" variant="contained" color="primary" disabled={submitting}>
              REGISTER
            </Button>
          </Box>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Already registered?{' '}
            <Link component={RouterLink} to="/login" underline="always">
              Sign in
            </Link>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
